/**
 * Lot Priorities panel for the Photo List Settings page — the per-rooftop
 * walking order as DRAG LADDERS (Ian, 2026-09-02): each rooftop's lot list
 * is its own mini-ladder, top lot cleared first, no score boxes. Scores
 * still exist in the database; the per-rooftop reorder endpoint assigns
 * them from position. Two sections over the same shape, each shown on its
 * own in the settings page's menu (`kind`): NEW priorities (walking order; a
 * listed lot is also "valid" for the NEW green rule) and USED front-lot
 * boosts (tiebreak inside a recon tier, frontline-tier steps only — that
 * rule stays built in). New rows land at the bottom of their
 * rooftop's ladder; drag them up. Drag works with mouse AND touch via
 * DragReorderList (pointer events).
 */
import React, { useMemo, useState } from "react"
import { Trash2 } from "lucide-react"

import {
  PhotoListLot,
  PhotoListNewLotPriority,
  PhotoListRooftopOption,
  PhotoListUsedLotBoost,
  createNewLotPriority,
  createUsedLotBoost,
  deleteNewLotPriority,
  deleteUsedLotBoost,
  reorderNewLotPriorities,
  reorderUsedLotBoosts,
} from "../../api/photoList"
import DragReorderList, { DragHandle } from "./DragReorderList"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

interface Props {
  /** Which ladder to show: the settings page gives each its own section. */
  kind: "new" | "used"
  newPriorities: PhotoListNewLotPriority[]
  usedBoosts: PhotoListUsedLotBoost[]
  lots: PhotoListLot[]
  rooftopOptions: PhotoListRooftopOption[]
  editable: boolean
  dealerGroupId: number
  onStructureChanged: () => void
  onError: (message: string | null) => void
}

/** One generic ladder row — a NEW priority or USED boost, value-free (the
 * ladder position IS the value now). */
interface LadderRow {
  id: number | null
  rooftop_name: string
  rooftop_label: string
  lot_name: string
}

export default function LotPrioritiesPanel({
  kind,
  newPriorities,
  usedBoosts,
  lots,
  rooftopOptions,
  editable,
  dealerGroupId,
  onStructureChanged,
  onError,
}: Props) {
  const isMobile = useIsMobile()
  const realLots = lots.filter((lot): lot is PhotoListLot & { id: number } => lot.id !== null)

  return (
    <>
      {kind === "new" && <PrioritySection
        title="NEW Walking Order"
        subtitle={
          "Drag each rooftop's lots into walking order — the top lot's cars " +
          "are photographed first on that rooftop's NEW sheet. A lot listed " +
          "here also counts as valid for the NEW green highlight."
        }
        rows={newPriorities}
        rooftopOptions={rooftopOptions}
        lots={realLots}
        editable={editable}
        isMobile={isMobile}
        onCreate={async (rooftopName, lotId) => {
          await createNewLotPriority(
            { rooftop_name: rooftopName, lot_id: lotId }, dealerGroupId,
          )
        }}
        onReorder={async (rooftopName, orderedIds) =>
          reorderNewLotPriorities(rooftopName, orderedIds, dealerGroupId)
        }
        onDelete={async (id) => deleteNewLotPriority(id, dealerGroupId)}
        onStructureChanged={onStructureChanged}
        onError={onError}
      />}
      {kind === "used" && <PrioritySection
        title="USED Front-Lot Boosts"
        subtitle={
          "On USED sheets, cars on higher lots come first within the same " +
          "recon step tier. Boosts only apply to frontline-tier steps " +
          "(Stickers/Photo, New/CPO Toyota, CPO Frontline, Frontline) — " +
          "that rule stays built in."
        }
        rows={usedBoosts}
        rooftopOptions={rooftopOptions}
        lots={realLots}
        editable={editable}
        isMobile={isMobile}
        onCreate={async (rooftopName, lotId) => {
          await createUsedLotBoost(
            { rooftop_name: rooftopName, lot_id: lotId }, dealerGroupId,
          )
        }}
        onReorder={async (rooftopName, orderedIds) =>
          reorderUsedLotBoosts(rooftopName, orderedIds, dealerGroupId)
        }
        onDelete={async (id) => deleteUsedLotBoost(id, dealerGroupId)}
        onStructureChanged={onStructureChanged}
        onError={onError}
      />}
    </>
  )
}

function PrioritySection({
  title,
  subtitle,
  rows,
  rooftopOptions,
  lots,
  editable,
  isMobile,
  onCreate,
  onReorder,
  onDelete,
  onStructureChanged,
  onError,
}: {
  title: string
  subtitle: string
  rows: LadderRow[]
  rooftopOptions: PhotoListRooftopOption[]
  lots: Array<PhotoListLot & { id: number }>
  editable: boolean
  isMobile: boolean
  onCreate: (rooftopName: string, lotId: number) => Promise<void>
  onReorder: (rooftopName: string, orderedIds: number[]) => Promise<void>
  onDelete: (id: number) => Promise<void>
  onStructureChanged: () => void
  onError: (message: string | null) => void
}) {
  const [draftRooftop, setDraftRooftop] = useState("")
  const [draftLotId, setDraftLotId] = useState("")
  const [adding, setAdding] = useState(false)

  // Group by rooftop, preserving the server's rooftop-then-score ordering.
  const groups = useMemo(() => {
    const byRooftop = new Map<string, LadderRow[]>()
    for (const row of rows) {
      const list = byRooftop.get(row.rooftop_name) ?? []
      list.push(row)
      byRooftop.set(row.rooftop_name, list)
    }
    return Array.from(byRooftop.values())
  }, [rows])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const lotId = Number.parseInt(draftLotId, 10)
    if (!draftRooftop || Number.isNaN(lotId)) return
    setAdding(true)
    onError(null)
    try {
      await onCreate(draftRooftop, lotId)
      // Reset only on success — a failed add keeps the picks for a retry.
      setDraftRooftop("")
      setDraftLotId("")
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add lot")
    } finally {
      setAdding(false)
    }
  }

  const handleReorder = (rooftopName: string) => async (orderedKeys: Array<number | string>) => {
    onError(null)
    try {
      await onReorder(rooftopName, orderedKeys as number[])
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save the new order")
      onStructureChanged()
    }
  }

  const handleDelete = (row: LadderRow) => async () => {
    if (row.id === null) return
    if (!window.confirm(`Remove "${row.lot_name}" from ${row.rooftop_label}'s order?`)) return
    onError(null)
    try {
      await onDelete(row.id)
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete row")
    }
  }

  const addDisabled = !draftRooftop || !draftLotId || adding

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>{title}</div>
      <p style={panelSubtitleStyle}>{subtitle}</p>

      {groups.map((groupRows) => (
        <div key={groupRows[0].rooftop_name}>
          <div style={rooftopHeaderStyle}>{groupRows[0].rooftop_label}</div>
          <DragReorderList
            items={groupRows}
            getKey={(row) => row.id ?? `default-${row.rooftop_name}-${row.lot_name}`}
            onReorder={handleReorder(groupRows[0].rooftop_name)}
            disabled={!editable}
            renderItem={(row, handleProps) => (
              <div style={rowStyle}>
                {editable && row.id !== null ? <DragHandle {...handleProps} /> : <span />}
                <span style={lotNameStyle}>{row.lot_name}</span>
                {editable && row.id !== null ? (
                  <button
                    type="button"
                    onClick={handleDelete(row)}
                    aria-label={`Delete ${row.lot_name} at ${row.rooftop_label}`}
                    style={trashButtonStyle}
                  >
                    <Trash2 size={15} strokeWidth={2} />
                  </button>
                ) : (
                  <span />
                )}
              </div>
            )}
          />
        </div>
      ))}

      {editable && (
        <form onSubmit={handleAdd} style={addFormStyle(isMobile)}>
          <select
            value={draftRooftop}
            onChange={(e) => setDraftRooftop(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 140 }}
          >
            <option value="">Rooftop…</option>
            {rooftopOptions.map((opt) => (
              <option key={opt.name} value={opt.name}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={draftLotId}
            onChange={(e) => setDraftLotId(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 140 }}
          >
            <option value="">Lot…</option>
            {lots.map((lot) => (
              <option key={lot.id} value={lot.id}>
                {lot.name}
              </option>
            ))}
          </select>
          <button type="submit" disabled={addDisabled} style={addButtonStyle(addDisabled)}>
            {adding ? "Adding…" : "Add at bottom"}
          </button>
        </form>
      )}
    </section>
  )
}

// --- Local styles (panel chrome from DealerPlatesView.tsx ~lines 500-519) ---

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 4,
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

const rooftopHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.md,
  marginBottom: THEME.spacing.xs,
}

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px minmax(0, 1fr) 40px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const lotNameStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

const selectStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const trashButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.semantic.danger,
}

const addFormStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.md,
})

const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})
